<template>
<div class="wrapper">
    <modal :no-footer="true" size="large" @close="$emit('close')">
        <template slot="header">
            {{ facility.facility_number }}
        </template>

        <template slot="content">
            <form :disabled="is_loading" @submit.prevent="onSaveFacility">
                <h3 class="mb-4">Facility Information</h3>

                <div class="flex">
                    <div class="flex-50 mr-3">
                        <label for="edit-facility-number">Facility Number</label>
                        <input id="edit-facility-number" type="text" v-model="updated_facility.facility_number" />
                    </div>

                    <div class="flex-50 ml-3">
                        <label for="edit-state">State</label>
                        <select-input
                            :active-index="active_state_index"
                            :choices="state_options"
                            :fixed="true"
                            @change="onChangeState"></select-input>
                    </div>
                </div>

                <div class="flex mt-3">
                    <div class="flex-50 mr-3">
                        <label for="edit-account-name">Account Name</label>
                        <input id="edit-account-name" type="text" v-model="updated_facility.account_name" />
                    </div>

                    <div class="flex-50 ml-3">
                        <label for="edit-corporate-group">Corporate Group</label>
                        <input id="edit-corporate-group" type="text" v-model="updated_facility.corporate_group" />
                    </div>
                </div>

                <div class="flex mt-3">
                    <div class="flex-33 mr-3">
                        <label for="edit-address">Address</label>
                        <input id="edit-address" type="text" v-model="updated_facility.address" />
                    </div>

                    <div class="flex-33 ml-3 mr-3">
                        <label for="edit-city">City</label>
                        <input id="edit-city" type="text" v-model="updated_facility.city" />
                    </div>

                    <div class="flex-33 ml-3">
                        <label for="edit-zip-code">Zip Code</label>
                        <input id="edit-zip-code" type="text" v-model="updated_facility.zip_code" />
                    </div>
                </div>

                <hr />

                <h3 class="mb-4">Manager Information</h3>

                <div class="flex jc-sb">
                    <div class="flex-50 mr-3">
                        <label for="edit-contact-name">Manager&rsquo;s Name</label>
                        <input id="edit-contact-name" type="text" required v-model="updated_facility.contact_name" />
                    </div>

                    <div class="flex-50 ml-3">
                        <label for="edit-email">Manager&rsquo;s Email</label>
                        <input id="edit-email" type="email" required v-model="updated_facility.email" />
                    </div>
                </div>

                <div class="flex jc-sb mt-3">
                    <div class="flex-50 mr-3">
                        <label for="edit-phone">Phone</label>
                        <input id="edit-phone" type="phone" required v-model="updated_facility.phone" />
                    </div>

                    <div class="flex-50 ml-3">
                        <label for="edit-fax">Fax (optional)</label>
                        <input id="edit-fax" type="text" v-model="updated_facility.fax" />
                    </div>
                </div>

                <h4 class="mb-4">Additional Contacts</h4>

                <additional-contacts :contacts="additional_contacts" @change="onAdditionalContactsChange" />

                <hr />

                <h3 class="mb-4">Audit Letter Configuration</h3>

                <div>
                    <div class="flex ai-c jc-sb">
                        <span class="mr-2 ws-nw">For the audit letter, the facility</span>
                        <select-input
                            :active-index="uses_updated_lease_language.active_index"
                            :choices="uses_updated_lease_language.options"
                            :fixed="false"
                            @change="onChangeUsesUpdatedLeaseLanguage"></select-input>
                    </div>

                    <div class="flex ai-fs jc-sb mt-3">
                        <div class="flex-0 mr-3 ws-nw">
                            <label for="lease-section-number">Lease Language Section</label>
                            <input id="lease-section-number" type="text" name="lease-section-number" v-model="updated_facility.lease_section_number" />
                        </div>

                        <div class="flex-a ml-3">
                            <label for="lease-section-text">Lease Language</label>
                            <textarea id="lease-section-text" name="lease-section-text" rows="4" v-model="updated_facility.lease_section_text" style="resize: vertical;"></textarea>
                        </div>
                    </div>

                    <div class="flex jc-sb mt-3">
                        <div class="flex-50 mr-3 pos-r">
                            <label for="brochure-url">Brochure URL</label>
                            <input :disabled="is_loading" id="brochure-url" type="url" required v-model="updated_facility.brochure_url" />

                            <a tabindex="-1" v-show="updated_facility.brochure_url" class="url-link pos-a" :href="updated_facility.brochure_url" target="_blank">
                                <i class="material-icons">launch</i>
                            </a>
                        </div>

                        <div class="flex-50 ml-3 pos-r">
                            <label for="certificate-url">Certificate URL</label>
                            <input :disabled="is_loading" id="certificate-url" type="url" required v-model="updated_facility.certificate_url" />

                            <a tabindex="-1" v-show="updated_facility.certificate_url" class="url-link pos-a" :href="updated_facility.certificate_url" target="_blank">
                                <i class="material-icons">launch</i>
                            </a>
                        </div>
                    </div>
                </div>

                <hr />

                <div class="mb-4 flex ai-fe jc-sb">
                    <h3 class="m-0">Coverage Rate Options</h3>

                    <div class="flex ai-c jc-fe">
                        <div v-show="is_loading_api_coverages" class="flex ai-c mr-2">
                            <img src="../assets/img/loading-black-32.gif" alt="loading" />
                        </div>

                        <button v-if="(facility.software || '').includes('sitelink')" :disabled="is_loading_api_coverages" type="button" @click="loadCoverages('sitelink')">
                            <i class="material-icons">get_app</i>
                            <span class="ml-1">Load SiteLink Coverages</span>
                        </button>

                        <button v-if="(facility.software || '').includes('storedge')" :disabled="is_loading_api_coverages" type="button" @click="loadCoverages('storedge')" class="ml-3">
                            <i class="material-icons">get_app</i>
                            <span class="ml-1">Load StorEdge Coverages</span>
                        </button>
                    </div>
                </div>

                <div>
                    <coverage-rate-options :options="coverages" @change="onCoverageOptionsChange" />

                    <p class="mt-5">Note: If you need to edit a coverage, please delete it.<br />You do not need to enter these in any particular order. They will be sorted as needed.</p>
                </div>

                <hr />

                <div>
                    <label for="edit-note">Facility Notes</label>
                    <textarea v-model="updated_facility.note" rows="4" />
                </div>

                <div class="flex jc-fe mt-5">
                    <div v-show="is_loading" class="flex ai-c mr-2">
                        <img src="../assets/img/loading-black-32.gif" alt="loading" />
                    </div>

                    <button :disabled="is_loading" type="submit">Save</button>
                </div>
            </form>
        </template>
    </modal>
</div>
</template>

<script>
import http from "@/http"
import { objectToParams } from "@/utilities/form-data"
import { statesByAbbr } from "@/utilities/states"
import CoverageRateOptions from "./add-facility-page/CoverageRateOptions.vue"

export default {
    name: "edit-facility-modal",
    components: {CoverageRateOptions},

    props: ["facility"],

    data () {
        return {
            active_state_index: 0,
            additional_contacts: [],
            coverages: [],
            is_loading: false,
            is_loading_additional_contacts: false,
            is_loading_api_coverages: false,
            is_loading_coverages: false,
            state_options: statesByAbbr,
            updated_facility: { ...this.facility },
            uses_updated_lease_language: {
                active_index: (this.facility.uses_updated_lease_language) ? 1 : 0,
                options: [
                    { key: "false", label: "has correct lease language.", badge: null },
                    { key: "true", label: "needs updated lease language.", badge: null }
                ]
            }
        }
    },

    created () {
        this.loadInitialStateIndex()
        this.normalizeLetterDates()
    },

    mounted () {
        Promise.all([
            this.getAdditionalContacts(),
            this.getCoverages()
        ])
    },

    methods: {
        async getAdditionalContacts () {
            this.is_loading_additional_contacts = true

            try {
                const { data: contacts } = await http.get(`/api/facilities/${this.facility.id}/additional-contacts`)

                this.additional_contacts = contacts
            } catch (ex) {
                console.warn(ex)
            } finally {
                this.is_loading_additional_contacts = false
            }
        },

        /**
         * Load the coverages for the facility.
         */
        async getCoverages () {
            this.is_loading_coverages = true

            try {
                const { data: coverages } = await http.get(`/api/facilities/${this.facility.id}/coverages`)

                this.coverages = coverages
            } catch (ex) {
                console.warn(ex)
            } finally {
                this.is_loading_coverages = false
            }
        },

        async loadCoverages (software) {
            this.is_loading_api_coverages = true

            try {
                const params = {
                    facility_number: this.facility.facility_number,
                    software
                }

                const { data } = await http("/api/coverages/fetch-from-api", { params })

                if (software.includes("sitelink")) {
                    this.coverages = this.coverages.filter((c) => c.sitelink_coverage_id === undefined)

                    for (const coverage of data) {
                        this.coverages.push({
                            coverage: parseFloat(coverage.dcCoverage),
                            premium: parseFloat(coverage.dcPremium),
                            sitelink_coverage_id: coverage.InsurCoverageID
                        })
                    }
                } else if (software === "storedge") {
                    this.coverages = this.coverages.filter((c) => c.storedge_coverage_id === undefined)

                    for (const coverage of data.invoiceable_items) {
                        const amount = coverage.amount
                        const coverageAmount = coverage.coverage_amount
                        const id = coverage.id

                        this.coverages.push({
                            coverage: parseFloat(coverageAmount),
                            premium: parseFloat(amount),
                            storedge_coverage_id: id
                        })
                    }
                } else {
                    console.warn("Unexpected software type: %s", software)
                }
            } catch (ex) {
                if (ex.message) {
                    this.$modalManager.add({ state: "bad", header: "Error", body: ex.message })
                } else {
                    this.$modalManager.add({ state: "bad", header: "Error", body: ex })
                }
            } finally {
                this.is_loading_api_coverages = false
            }
        },

        /**
         * Set the active state select option to the facility's state.
         */
        loadInitialStateIndex () {
            statesByAbbr.forEach((state, index) => {
                if (this.facility.state === state.key) {
                    this.active_state_index = index
                }
            })
        },

        /**
         * Normalize the letter send dates because JavaScript automatically subtracts hours
         * for local time, making it appear as if the date selected is the day before.
         */
        normalizeLetterDates () {
            if (this.updated_facility.letter_effective_date) {
                const date = new Date(this.updated_facility.letter_effective_date)
                date.setUTCHours(date.getHours())
                this.updated_facility.letter_effective_date = date
            }

            if (this.updated_facility.initial_letter_send_date) {
                const date = new Date(this.updated_facility.initial_letter_send_date)
                date.setUTCHours(date.getHours())
                this.updated_facility.initial_letter_send_date = date
            }

            if (this.updated_facility.reminder_letter_send_date) {
                const date = new Date(this.updated_facility.reminder_letter_send_date)
                date.setUTCHours(date.getHours())
                this.updated_facility.reminder_letter_send_date = date
            }

            if (this.updated_facility.welcome_letter_send_date) {
                const date = new Date(this.updated_facility.welcome_letter_send_date)
                date.setUTCHours(date.getHours())
                this.updated_facility.welcome_letter_send_date = date
            }
        },

        /**
         * Update the state field when a state is picked from the list.
         *
         * @param int index Chose state option index
         */
        onChangeState (index) {
            this.active_state_index = index

            this.updated_facility.state = this.state_options[index].key
        },

        onChangeUsesUpdatedLeaseLanguage (index) {
            this.uses_updated_lease_language.active_index = index

            this.updated_facility.uses_updated_lease_language = this.uses_updated_lease_language.options[index].key
        },

        onAdditionalContactsChange (contacts) {
            this.additional_contacts = contacts
        },

        onCoverageOptionsChange (coverages) {
            this.coverages = coverages
        },

        async onSaveFacility () {
            const data = Object.assign({
                coverages: JSON.stringify(this.coverages)
                // coverages: this.coverages.map((coverage) => `${coverage.premium}-${coverage.coverage}`).join(",")
            }, this.updated_facility)

            data.additional_contacts = JSON.stringify(this.additional_contacts)

            const params = objectToParams(data, { ignore: ["tenant_count"] })

            this.is_loading = true

            try {
                await http.post(`/api/facilities/${this.facility.id}`, params)

                this.$emit("save")
                this.$emit("close")
            } catch (ex) {
                console.log(ex)
            } finally {
                this.is_loading = false
            }
        }
    }
}
</script>

<style scoped>
.new-account-table {
    border-collapse: collapse;
    table-layout: auto;
}

.new-account-table td {
    border: 1px solid #dedede;
    padding: 6px 8px;
}

.url-link {
    bottom: 11px;
    right: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/EditFacilityModal.vue